import { DOCUMENT } from '@angular/common';
import { Component, ElementRef, HostListener, OnDestroy, TemplateRef, ViewChild, effect, inject } from '@angular/core';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import {
  faAddressCard,
  faArrowRightFromBracket,
  faBriefcase,
  faChevronRight,
  faCog,
  faFileAlt,
  faHome,
  faInfoCircle,
  faKey,
  faSignInAlt,
  faUser,
  faXmark
} from '@fortawesome/free-solid-svg-icons';
import {
  NgbAccordionBody,
  NgbAccordionButton,
  NgbAccordionCollapse,
  NgbAccordionDirective,
  NgbAccordionHeader,
  NgbAccordionItem,
  NgbDropdown,
  NgbDropdownItem,
  NgbDropdownMenu,
  NgbDropdownToggle,
  NgbModal
} from '@ng-bootstrap/ng-bootstrap';
import { Router, RouterLink } from '@angular/router';
import { MainHeaderComponent } from '../main-header/main-header.component';
import { AuthenticationService } from '../../security/authentication.service';
import { MobileNavigationService } from './mobile-navigation.service';
import { AlertService } from '../../services/alert.service';
import { finalize } from 'rxjs/operators';

interface MenuItem {
  external?: boolean;
  label: string;
  route: string;
  exact?: boolean;
}

interface MenuSection {
  icon: IconDefinition;
  id: string;
  items: MenuItem[];
  label: string;
  prefixes: string[];
}

@Component({
  selector: 'app-main-menu',
  templateUrl: './main-menu.component.html',
  styleUrls: ['./main-menu.component.scss'],
  imports: [
    FontAwesomeModule,
    RouterLink,
    MainHeaderComponent,
    NgbDropdownMenu,
    NgbDropdownItem,
    NgbDropdown,
    NgbDropdownToggle,
    NgbAccordionDirective,
    NgbAccordionItem,
    NgbAccordionHeader,
    NgbAccordionButton,
    NgbAccordionCollapse,
    NgbAccordionBody
  ],
  standalone: true
})
export class MainMenuComponent implements OnDestroy {
  private static readonly MOBILE_BREAKPOINT = 992;

  authenticationService = inject(AuthenticationService);
  private readonly document = inject(DOCUMENT);
  private readonly mobileNavigationService = inject(MobileNavigationService);
  private readonly modalService = inject(NgbModal);
  private readonly alertService = inject(AlertService);
  private readonly router = inject(Router);

  @ViewChild('mobileAccordion') private mobileAccordion?: NgbAccordionDirective;
  @ViewChild('mobileOverlay') private mobileOverlay?: ElementRef<HTMLElement>;

  private overlayAnimationFrame?: number;
  protected isPasswordResetSubmitting = false;

  protected readonly faAddressCard = faAddressCard;
  protected readonly faArrowRightFromBracket = faArrowRightFromBracket;
  protected readonly faBriefcase = faBriefcase;
  protected readonly faChevronRight = faChevronRight;
  protected readonly faCog = faCog;
  protected readonly faFileAlt = faFileAlt;
  protected readonly faHome = faHome;
  protected readonly faInfoCircle = faInfoCircle;
  protected readonly faKey = faKey;
  protected readonly faSignInAlt = faSignInAlt;
  protected readonly faUser = faUser;
  protected readonly faXmark = faXmark;

  constructor() {
    effect(() => {
      const isMenuOpen = this.mobileNavigationService.isOpen();

      if (typeof window !== 'undefined' && this.overlayAnimationFrame !== undefined) {
        window.cancelAnimationFrame(this.overlayAnimationFrame);
        this.overlayAnimationFrame = undefined;
      }

      if (!this.isMobileViewport()) {
        this.releaseBodyScrollLock();

        if (isMenuOpen) {
          this.mobileNavigationService.close();
        }

        return;
      }

      if (!isMenuOpen) {
        this.releaseBodyScrollLock();
        return;
      }

      this.applyBodyScrollLock();

      if (typeof window !== 'undefined') {
        this.overlayAnimationFrame = window.requestAnimationFrame(() => {
          const sectionToExpand = this.getDefaultExpandedSectionId();

          if (sectionToExpand) {
            this.mobileAccordion?.collapseAll();
            this.mobileAccordion?.expand(sectionToExpand);
          }

          const mobileToggleButton = this.mobileOverlay?.nativeElement.querySelector<HTMLButtonElement>('.brand__menu-toggle');

          if (mobileToggleButton) {
            mobileToggleButton.focus();
            return;
          }

          this.mobileOverlay?.nativeElement.focus();
        });
      }
    });
  }

  protected get isMobileMenuOpen(): boolean {
    return this.mobileNavigationService.isOpen();
  }

  protected get currentUserLabel(): string {
    const currentUser = this.authenticationService.currentUserValue;
    return currentUser?.userAccountLightDto?.firstName ?? currentUser?.username ?? 'Guest';
  }

  protected get isAuthenticated(): boolean {
    return this.authenticationService.isAuthenticated();
  }

  protected get canResetPassword(): boolean {
    return this.authenticationService.currentUserValue?.userAccountLightDto?.oauthProvider?.toLowerCase() === 'auth0';
  }

  protected get primarySections(): MenuSection[] {
    const sections: MenuSection[] = [];

    if (this.hasRole('ROLE_ROOT_ADMIN')) {
      sections.push({
        icon: this.faCog,
        id: 'admin',
        label: 'Admin',
        prefixes: [
          '/account-management',
          '/email-management',
          '/user-management',
          '/faq-management'
        ],
        items: [
          { label: 'Account Management', route: '/account-management' },
          { label: 'Email Management', route: '/email-management' },
          { label: 'User Management', route: '/user-management' },
          { label: 'FAQ Management', route: '/faq-management' }
        ]
      });

      sections.push({
        icon: this.faFileAlt,
        id: 'reports',
        label: 'Reports',
        prefixes: [
          '/agency-report',
          '/program-report',
          '/program-report-custom',
          '/user-report',
          '/program-referrals-report',
          '/juvenile-programs-report',
          '/statistics-report'
        ],
        items: [
          { label: 'Agency Report', route: '/agency-report' },
          { label: 'Programs Report', route: '/program-report' },
          { label: 'Programs Report - Custom', route: '/program-report-custom' },
          { label: 'Users Report', route: '/user-report' },
          { label: 'Program Referrals Report', route: '/program-referrals-report' },
          { label: 'Juvenile Programs Report', route: '/juvenile-programs-report' },
          { label: 'Statistics', route: '/statistics-report' }
        ]
      });
    }

    if (this.hasRole('ROLE_ReferralsScreens')) {
      sections.push({
        icon: this.faAddressCard,
        id: 'referrals',
        label: 'Referrals',
        prefixes: ['/all-referrals-view', '/weekly-reporting', '/access-probationer', '/probationer'],
        items: [
          { label: 'All Referrals View', route: '/all-referrals-view' },
          { label: 'Weekly Referral Reporting', route: '/weekly-reporting' },
          { label: 'Access Probationer Record', route: '/access-probationer' }
        ]
      });
    }

    if (this.hasRole('ROLE_PROGRAM_ADMIN')) {
      sections.push({
        icon: this.faBriefcase,
        id: 'account-setup',
        label: 'Account Setup',
        prefixes: ['/status', '/agency-wizard', '/agency-info', '/moa', '/mou', '/insurance', '/programs', '/users'],
        items: this.filterMenuItems([
          { label: 'Account Status', route: '/status' },
          this.hasRole('ROLE_wizard') ? { label: 'Wizard', route: '/agency-wizard' } : null,
          this.hasRole('ROLE_AgencySetupScreens') ? { label: 'Agency Info', route: '/agency-info' } : null,
          this.hasRole('ROLE_MoaScreens') ? { label: 'Agreements', route: '/moa' } : null,
          this.hasRole('ROLE_MouScreens') ? { label: 'Agreements', route: '/mou' } : null,
          this.hasRole('ROLE_ProbationScreens') && this.hasRole('ROLE_MoaScreens')
            ? { label: 'Insurance Upload', route: '/insurance' }
            : null,
          this.hasRole('ROLE_ProbationScreens') ? { label: 'Programs', route: '/programs' } : null,
          this.authenticationService.hasAgency() ? { label: 'Setup Users', route: '/users' } : null
        ])
      });
    }

    sections.push({
      icon: this.faInfoCircle,
      id: 'support',
      label: 'Support',
      prefixes: ['/about', '/getting-started', '/faq', '/contact-us', '/help-user', '/help-program'],
      items: [
        { label: 'About the Program', route: '/about' },
        { label: 'Getting Started', route: '/getting-started' },
        { label: 'FAQ', route: '/faq' },
        { label: 'Contact Us', route: '/contact-us' },
        { label: 'Multiple Users Setup', route: '/help-user' },
        { label: 'Adding New Programs', route: '/help-program' },
        {
          external: true,
          label: 'First-Time Sign-In Instructions',
          route: 'https://www.sandiegocounty.gov/probation/docs/CRD_First-Time_Sign-In_Instructions.pdf'
        }
      ]
    });

    return sections;
  }

  ngOnDestroy(): void {
    this.releaseBodyScrollLock();

    if (typeof window !== 'undefined' && this.overlayAnimationFrame !== undefined) {
      window.cancelAnimationFrame(this.overlayAnimationFrame);
    }
  }

  protected closeMenu(): void {
    this.mobileNavigationService.close();
  }

  protected closeMenuAndNavigate(): void {
    this.closeMenu();
  }

  protected logout(): void {
    this.closeMenu();
    void this.authenticationService.logout();
  }

  protected requestPasswordReset(confirmContent: TemplateRef<unknown>): void {
    this.closeMenu();
    this.modalService.open(confirmContent, { centered: true }).result.then(
      () => this.sendPasswordResetRequest(),
      () => undefined
    );
  }

  protected isCurrentRoute(route: string, exact = false): boolean {
    return exact ? this.router.url === route : this.router.url.startsWith(route);
  }

  protected isSectionActive(section: MenuSection): boolean {
    return this.isCurrentSection(section.prefixes);
  }

  @HostListener('window:keydown.escape')
  protected handleEscapeKey(): void {
    if (this.isMobileMenuOpen) {
      this.closeMenu();
    }
  }

  @HostListener('window:resize')
  protected onWindowResize(): void {
    if (!this.isMobileViewport()) {
      this.closeMenu();
    }
  }

  private applyBodyScrollLock(): void {
    this.document.body.classList.add('mobile-nav-open');
  }

  private filterMenuItems(items: Array<MenuItem | null>): MenuItem[] {
    return items.filter((item): item is MenuItem => item !== null);
  }

  private getDefaultExpandedSectionId(): string | null {
    return this.primarySections.find(section => this.isSectionActive(section))?.id ?? this.primarySections[0]?.id ?? null;
  }

  private hasRole(role: string): boolean {
    return this.authenticationService.hasRole(role);
  }

  private isCurrentSection(prefixes: string[]): boolean {
    return prefixes.some(prefix => this.router.url.startsWith(prefix));
  }

  private isMobileViewport(): boolean {
    return typeof window === 'undefined' || window.innerWidth < MainMenuComponent.MOBILE_BREAKPOINT;
  }

  private releaseBodyScrollLock(): void {
    this.document.body.classList.remove('mobile-nav-open');
  }

  private sendPasswordResetRequest(): void {
    this.isPasswordResetSubmitting = true;
    this.authenticationService.requestPasswordReset()
      .pipe(finalize(() => this.isPasswordResetSubmitting = false))
      .subscribe({
        next: () => this.alertService.addSelfClosing({
          message: 'Password reset email sent. Follow the instructions in the email to reset your password.',
          id: null,
          type: 'success'
        }),
        error: () => this.alertService.addSelfClosing({
          message: 'Password reset email could not be sent. Please try again or contact support.',
          id: null,
          type: 'danger'
        })
      });
  }
}
